/**
 * Product page state — loading the product, its shop and both review summaries, variant (axis) selection, the
 * price / MRP / stock of the picked variant, the image index, add-to-cart with the one-shop prompt, Buy Now and
 * the variant-picker fallback. Moved verbatim out of ProductDetailsPage so the classic view and the Blinkit view
 * share one implementation.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenant } from "../../../contexts/TenantContext";
import { multiShopOn } from "../../../lib/multiShop";
import { buildAxes, defaultSelection, hasMatrix, resolveVariant, applyAxisChange } from "../../../lib/variantAxes";
import { useLowStockThreshold } from "../../../components/StockBadge";
import { useCartCount } from "../../../lib/useCartCount";
import { isDeactivated } from "../../../lib/productAvailability";

export function useProductController() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const { tenant } = useTenant();

    const [product, setProduct] = useState(null);
    const [shop, setShop] = useState(null);
    const [imgIdx, setImgIdx] = useState(0);
    const [prodSummary, setProdSummary] = useState(null);
    const [shopSummary, setShopSummary] = useState(null);
    const [qty, setQty] = useState(0);
    const [busy, setBusy] = useState(false);
    const [variantPickerOpen, setVariantPickerOpen] = useState(false);
    const [error, setError] = useState(null);
    // iter130 — tenant-configured low-stock threshold for the badge +
    // details grid tone.
    const lowStockThreshold = useLowStockThreshold();
    // Live cart-item count for the top-right "View Cart" badge — same
    // hook MarketplaceHome's header uses, so it stays in sync with the
    // rest of the app via the shared ``cart:update`` event.
    const cartCount = useCartCount();

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data: p } = await api.get(`/products/${id}`);
                if (!alive) return;
                setProduct(p);
                // Fetch shop card (name, rating, cover) in parallel with summaries.
                const rid = p.restaurant_id;
                const [rest, ps, ss] = await Promise.all([
                    api.get(`/restaurants/${rid}`).catch(() => ({ data: null })),
                    api.get(`/products/${id}/review-summary`).catch(() => ({ data: null })),
                    api.get(`/restaurants/${rid}/review-summary`).catch(() => ({ data: null })),
                ]);
                if (!alive) return;
                setShop(rest.data?.restaurant || null);
                setProdSummary(ps.data);
                setShopSummary(ss.data);
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.status === 404 ? "Product not found" : "Failed to load product");
            }
        })();
        return () => { alive = false; };
    }, [id]);

    const enabledVariants = useMemo(
        () => (product?.variants || []).filter((v) => v?.enabled !== false),
        [product],
    );
    const axes = useMemo(() => buildAxes(enabledVariants), [enabledVariants]);
    const hasVariantChoice = enabledVariants.length >= 1 && axes.length > 0;
    const [selection, setSelection] = useState({});
    useEffect(() => {
        // Seed the selection with a sensible default (is_default variant
        // or first) once the product loads.
        if (enabledVariants.length && Object.keys(selection).length === 0) {
            setSelection(defaultSelection(enabledVariants, axes));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.id]);
    const pickedVariant = useMemo(
        () => resolveVariant(enabledVariants, axes, selection),
        [enabledVariants, axes, selection],
    );
    const pickedVariantId = pickedVariant?.id || null;
    const matrixMode = hasMatrix(enabledVariants);
    const setAxis = (axisKey, value) => {
        setSelection((prev) => applyAxisChange(enabledVariants, axes, prev, axisKey, value));
        setQty(0);   // Any variant change resets the local qty control.
    };

    const displayPrice = pickedVariant
        ? pickedVariant.selling_price
        : (hasVariantChoice
            ? (() => {
                // iter134e — ignore variants without a set selling_price
                // so a half-configured variant can't pull "from ₹0" over
                // a real ₹500 variant.
                const priced = enabledVariants
                    .map((v) => Number(v.selling_price))
                    .filter((n) => Number.isFinite(n) && n > 0);
                return priced.length > 0
                    ? Math.min(...priced)
                    : Number(product?.selling_price || 0);
            })()
            : product?.selling_price);
    const displayMrp = pickedVariant ? pickedVariant.mrp : product?.mrp;
    // Stock is per-variant when a variant is selected, else per-product.
    const effectiveStock = pickedVariant?.stock ?? product?.stock;

    const trackStock = product?.track_stock !== false;
    const isSoldOut = trackStock && (
        product?.status === "OUT_OF_STOCK"
        || (effectiveStock != null && Number(effectiveStock) <= 0)
    );
    // Switched off by the shop (old link, stale page) — the server refuses it too.
    const isUnavailable = isDeactivated(product);
    const remaining = trackStock && effectiveStock != null
        ? Math.max(0, Number(effectiveStock) - qty)
        : Infinity;
    const cantAddMore = remaining <= 0;

    const images = useMemo(() => {
        if (!product) return [];
        const list = [...(product.images || [])];
        // Prepend the picked variant image (if any) as the primary shot.
        if (pickedVariant?.image_url && !list.includes(pickedVariant.image_url)) {
            list.unshift(pickedVariant.image_url);
        }
        return list;
    }, [product, pickedVariant]);

    // iter121 — reset the carousel when a variant swap changes the image
    // list, and clamp imgIdx if the list ever shrinks.
    useEffect(() => {
        if (imgIdx > 0 && imgIdx >= images.length) setImgIdx(0);
    }, [images, imgIdx]);

    // Returns true when the cart ends up holding the item the caller
    // asked for (so ``buyNow`` below knows it's safe to proceed to
    // checkout), false on any early-exit (login redirect, missing
    // variant pick, declined restaurant-conflict prompt, API error).
    const postAdd = async (delta) => {
        const newQty = Math.max(0, qty + delta);
        if (delta > 0 && cantAddMore) return false;
        // PDP has inline axis pickers — if the customer hasn't
        // completed the selection for a multi-variant product, nudge
        // them to pick and don't post to /cart with a wrong price.
        if (delta > 0 && hasVariantChoice && !pickedVariant) {
            toast.info(matrixMode ? "Choose every option first" : "Choose an option first");
            return false;
        }
        if (!user) {
            nav("/login", { state: { from: `/product/${id}` } });
            return false;
        }
        setBusy(true);
        try {
            if (delta > 0) {
                const variant = pickedVariant;
                const item = {
                    product_id: product.id,
                    name: variant ? `${product.name} — ${variant.label}` : product.name,
                    quantity: newQty - qty,
                    selling_price: variant ? variant.selling_price : product.selling_price,
                    image_url: variant?.image_url || product.images?.[0],
                    food_type: product.food_type,
                    variant_id: pickedVariantId || undefined,
                };
                const url = `/cart/items?restaurant_id=${product.restaurant_id}`;
                try {
                    await api.post(url, item);
                    window.dispatchEvent(new Event("cart:update"));
                    setQty(newQty);
                    toast.success(`Added ${product.name}`);
                    return true;
                } catch (e) {
                    const status = e?.response?.status;
                    const detail = e?.response?.data?.detail;
                    if (status === 409 && detail && typeof detail === "object" && detail.code === "DIFFERENT_RESTAURANT") {
                        const existing = detail.existing_restaurant || "another restaurant";
                        const ok = window.confirm(`Your cart has items from ${existing}.\nWe can only deliver from one restaurant at a time. Clear cart and start a new order from ${shop?.name || "this restaurant"}?`);
                        if (!ok) return false;
                        await api.post(url + "&force=true", item);
                        window.dispatchEvent(new Event("cart:update"));
                        setQty(newQty);
                        toast.success(`Cart cleared. Added ${product.name}`);
                        return true;
                    }
                    toast.error(typeof detail === "string" ? detail : "Failed to add");
                    return false;
                }
            } else if (newQty === 0) {
                // Empty cart entirely to match the ProductCard behaviour — except for multi-shop checkout tenants,
                // whose cart may hold other shops' items: there only THIS product is removed.
                if (multiShopOn(tenant)) await api.patch(`/cart/items/${product.id}?quantity=0${pickedVariantId ? `&variant_id=${pickedVariantId}` : ""}`);
                else await api.delete("/cart");
                window.dispatchEvent(new Event("cart:update"));
                setQty(0);
                return true;
            }
            return true;
        } finally {
            setBusy(false);
        }
    };

    const [buyNowBusy, setBuyNowBusy] = useState(false);
    const buyNow = async () => {
        if (isSoldOut || isUnavailable || busy || buyNowBusy) return;
        if (qty === 0) {
            if (hasVariantChoice && !pickedVariant) {
                toast.info(matrixMode ? "Choose every option first" : "Choose an option first");
                return;
            }
            setBuyNowBusy(true);
            const ok = await postAdd(1);
            setBuyNowBusy(false);
            if (!ok) return;
        }
        // Already-in-cart (qty > 0) or just-added — CheckoutPage fetches
        // the live cart itself, so no state needs to be passed here.
        nav("/checkout");
    };

    const onVariantPicked = async (variant) => {
        setVariantPickerOpen(false);
        // Reflect the picker's choice into the inline axis selection so
        // the on-page chips highlight it too.
        setSelection(matrixMode ? { ...(variant.option_values || {}) } : { [variant.type || "CUSTOM"]: variant.id });
        setImgIdx(0);
        setTimeout(() => postAdd(1), 0);
    };

    return {
        id, nav, user, product, shop, imgIdx, setImgIdx, prodSummary, shopSummary, qty, setQty, busy,
        variantPickerOpen, setVariantPickerOpen, error, lowStockThreshold, cartCount, enabledVariants, axes,
        hasVariantChoice, selection, setSelection, pickedVariant, pickedVariantId, matrixMode, setAxis, displayPrice,
        displayMrp, effectiveStock, trackStock, isSoldOut, isUnavailable, remaining, cantAddMore, images, postAdd,
        buyNowBusy, buyNow, onVariantPicked,
    };
}
